import { Input, TextArea } from '@successmode-ktp/kui';
import ICON_SEND from '@/assets/sideBar/send.png';
import styled from 'styled-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getRefundAmount } from '@/util/getRefundAmount';
import {
  attachedHyphensDate,
  comma,
  isValidDate,
  removeComma,
  validateEnglishInput,
  validateNumberInput,
} from '@/util/format';
import Selection from '@/components/sideNavigation/Cs/chatMessage/Selection';
import { nationList, supplyList } from '@/constants/request.const';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { useMutation } from 'react-query';
import { sendChat } from '@/api/cs';
import { updateCs } from '@/store/modules/cs';
import RadioSection from './RadioSection';
import BirthDateInput from './BirthDateInput';
import useGetGlobalStatus from '@/hooks/useGetGlobalStatus';

const Inputs = [
  {
    name: 'supplyDate',
    description: '시술날짜를 8자리로 입력해 주세요',
    label: '시술 날짜',
    required: true,
    placeholder: 'YYYYMMDD',
  },
  {
    name: 'touristName',
    label: '고객 성함',
    required: true,
    placeholder: '고객 성함 전체를 영어로 입력하세요',
  },
  {
    name: 'touristGender',
    label: '고객 성별',
    required: true,
    placeholder: '고객 성별을 선택하세요',
  },
  {
    name: 'passportNumber',
    label: '여권번호',
    required: true,
    placeholder: '여권번호를 입력하세요',
  },
  {
    name: 'birthDate',
    label: '생년월일',
    required: true,
    placeholder: 'ex) 2024-01-26',
  },
  {
    name: 'nationality',
    label: '고객 국적',
    required: true,
    placeholder: '고객 국적을 선택하세요',
  },
  {
    name: 'chartNumber',
    label: '고객차트번호',
    required: false,
    placeholder: '고객차트번호를 입력하세요',
  },
  {
    name: 'productType',
    label: '시술명',
    required: true,
    placeholder: '시술명을 선택하세요',
  },
  {
    name: 'productAmount',
    label: '시술 금액',
    required: true,
    placeholder: '시술 금액을 입력하세요',
  },
];

function RequestRefundSection() {
  const dispatch = useAppDispatch();
  const FormRef = useRef<HTMLDivElement>(null);
  const { productTypes, hospitalId } = useAppSelector((state) => state.user);
  const { globalStatus } = useGetGlobalStatus();
  const isChartNumberEnabled = globalStatus?.isChartNumberEnabled;
  const [values, setValues] = useState<Record<string, any>>({
    supplyDate: '',
    touristName: '',
    touristGender: 'F',
    passportNumber: '',
    birthDate: '',
    nationality: '',
    productType: { label: '', value: '' },
    chartNumber: '',
    productAmount: '',
  });
  const [reason, setReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [openedDropdown, setOpenedDropdown] = useState<
    null | 'nationality' | 'productType'
  >(null);
  const [isSupplyDateError, setIsSupplyDateError] = useState(false);
  const [isTouristNameError, setIsTouristNameError] = useState(false);
  const [isPassportNumberError, setIsPassportNumberError] = useState(false);
  const [isChartNumberError, setIsChartNumberError] = useState(false);
  const [isBirthDateError, setIsBirthDateError] = useState(false);

  const onChangeInput = ({ name, value }: { name: string; value: any }) => {
    if (name === 'supplyDate') {
      setIsSupplyDateError(false);

      const supplyDate = attachedHyphensDate(value);
      setValues((prev) => {
        return { ...prev, supplyDate };
      });
      return;
    }

    if (name === 'birthDate') {
      setIsBirthDateError(false);

      const birthDate = attachedHyphensDate(value);
      setValues((prev) => {
        return { ...prev, birthDate };
      });
      return;
    }

    if (name === 'touristName') {
      const hasKorean = /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(value);
      setIsTouristNameError(hasKorean);

      const touristName = value.toUpperCase();
      setValues((prev) => {
        return { ...prev, touristName };
      });
      return;
    }

    if (name === 'touristGender') {
      setValues((prev) => {
        return { ...prev, touristGender: value };
      });
      return;
    }
    if (name === 'passportNumber') {
      const hasKorean = /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(value);
      setIsPassportNumberError(hasKorean);

      const passportNumber = validateEnglishInput(value).toUpperCase();
      setValues((prev) => {
        return { ...prev, passportNumber };
      });
      return;
    }
    if (name === 'chartNumber') {
      const hasNotNumber = /[^0-9]/.test(value);
      setIsChartNumberError(hasNotNumber);
      const chartNumber = validateNumberInput(value);
      setValues((prev) => {
        return { ...prev, chartNumber };
      });
      return;
    }

    if (name === 'productAmount') {
      const productAmount = comma(validateNumberInput(value));
      const refundAmount =
        removeComma(value) > 0
          ? comma(getRefundAmount(removeComma(value)))
          : '';
      setValues((prev) => {
        return { ...prev, productAmount };
      });
      setRefundAmount(refundAmount);
      return;
    }

    setValues((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const { mutate: mutateSendChat } = useMutation(sendChat);
  const onRequestCreateRefund = () => {
    // API 요청 데이터 객체 생성
    const requestData: any = {
      messageType: 'REFUND_CREATE_ACCEPTED',
      supplyDate: values.supplyDate,
      touristName: values.touristName,
      passportNumber: values.passportNumber,
      birthDate: values.birthDate,
      nationality: values.nationality,
      productType: values.productType.label,
      productAmount: removeComma(values.productAmount),
      refundAmount: removeComma(refundAmount),
      hospitalName: localStorage.getItem('name') || '',
      gender: values.touristGender,
      reason: reason.trim(),
    };

    // chartNumber가 활성화된 경우에만 데이터에 포함
    if (isChartNumberEnabled && values.chartNumber) {
      requestData.chartNumber = values.chartNumber;
    }

    mutateSendChat(requestData, {
      onSuccess: (data) => {
        dispatch(
          updateCs({
            chatId: data.chatId,
          }),
        );
      },
    });
  };

  const isDisabledButton = useMemo(() => {
    // 필수 입력 필드만 검증
    const requiredFields = Inputs.filter(
      (input) =>
        input.required &&
        (input.name !== 'chartNumber' || isChartNumberEnabled),
    ).map((input) => input.name);

    return requiredFields.some((field) => values[field] === '');
  }, [values, Inputs, isChartNumberEnabled]);

  useEffect(() => {
    if (FormRef?.current && removeComma(refundAmount) !== 0) {
      const target = FormRef.current as HTMLElement;
      setTimeout(() => {
        target.scrollIntoView({
          block: 'nearest',
        });
      }, 10);
    }
  }, [FormRef, refundAmount]);

  const scrollToTop = (id: string) => {
    const ListWrapper = document.querySelector('#chat-message-list');
    if (ListWrapper) {
      if (id === 'productAmount') {
        ListWrapper.scrollTo({
          top: ListWrapper.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        ListWrapper.scrollTo({
          top: id === 'nationality' ? 450 : 600,
          behavior: 'smooth',
        });
      }
    }
  };
  const hasInputError = (
    name: string,
    errors: {
      isSupplyDateError: boolean;
      isTouristNameError: boolean;
      isPassportNumberError: boolean;
      isBirthDateError: boolean;
      isChartNumberError: boolean;
    },
  ): boolean => {
    switch (name) {
      case 'supplyDate':
        return errors.isSupplyDateError;
      case 'touristName':
        return errors.isTouristNameError;
      case 'passportNumber':
        return errors.isPassportNumberError;
      case 'birthDate':
        return errors.isBirthDateError;
      case 'chartNumber':
        return errors.isChartNumberError;
      default:
        return false;
    }
  };
  const getErrorMessage = (
    name: string,
    errors: {
      isTouristNameError: boolean;
      isBirthDateError: boolean;
      isSupplyDateError: boolean;
      isPassportNumberError: boolean;
      isChartNumberError: boolean;
    },
  ): string => {
    if (!hasInputError(name, errors)) {
      return '';
    }

    switch (name) {
      case 'touristName':
        return '영어로 입력하세요';
      case 'birthDate':
        return '생년월일 형식이 올바르지 않습니다';
      case 'supplyDate':
        return '시술 날짜 형식이 올바르지 않습니다';
      case 'passportNumber':
        return '영어로 입력하세요';
      case 'chartNumber':
        return '숫자로 입력하세요';
      default:
        return '';
    }
  };
  const SUPPLY_LIST = supplyList({ productTypes, hospitalId });

  return (
    <Body>
      <Description>
        안녕하세요.
        <br />
        <strong>등록할 환급건을 입력해주세요</strong>
      </Description>
      <InputList>
        {Inputs.filter((input) => {
          // chartNumber 필드는 isChartNumberEnabled가 true일 때만 표시
          if (input.name === 'chartNumber' && !isChartNumberEnabled) {
            return false;
          }
          return true;
        }).map(({ name, description, label, required, placeholder }) => (
          <InputItem key={name}>
            {name === 'nationality' ? (
              <Selection
                id='refund-request-nationality'
                title='고객 국적'
                selected={{ label: values[name], value: values[name] }}
                onChange={({ label }: any) =>
                  onChangeInput({ name, value: label })
                }
                options={nationList}
                placeholder='고객 국적을 입력해주세요'
                isOpenDropdown={openedDropdown === 'nationality'}
                setIsOpenDropdown={(value) => {
                  if (openedDropdown !== 'nationality') {
                    scrollToTop('nationality');
                  }
                  setOpenedDropdown(value ? 'nationality' : null);
                }}
              />
            ) : name === 'touristGender' ? (
              <RadioSection
                options={[
                  { id: '1', label: '여성', value: 'F' },
                  { id: '2', label: '남성', value: 'M' },
                ]}
                title='고객 성별'
                name='touristGender'
                isRequired={true}
                onChange={(value: any) => onChangeInput({ name, value: value })}
                defaultValue={values[name]}
              />
            ) : name === 'birthDate' ? (
              <BirthDateInput
                label='생년월일'
                isRequired={true}
                initialValue={values[name]}
                onChange={(date) => onChangeInput({ name, value: date })}
                error={isBirthDateError ? '유효하지 않은 날짜입니다' : ''}
                setError={(error) => setIsBirthDateError(!!error)}
                onValidate={() => {
                  console.log('birthDateValid', values.birthDate);
                  setIsBirthDateError(!isValidDate(values.birthDate));
                }}
              />
            ) : name === 'productType' ? (
              <Selection
                id='refund-request-productType'
                title='시술명'
                selected={values[name]}
                onChange={({ value, label }: any) =>
                  onChangeInput({ name, value: { label, value } })
                }
                options={SUPPLY_LIST}
                placeholder='시술명을 선택하세요'
                isOpenDropdown={openedDropdown === 'productType'}
                setIsOpenDropdown={(value) => {
                  if (openedDropdown !== 'productType') {
                    scrollToTop('productType');
                  }
                  setOpenedDropdown(value ? 'productType' : null);
                }}
              />
            ) : (
              <Input
                label={label}
                description={description}
                descriptionStyle={
                  name === 'supplyDate'
                    ? { marginTop: '-4px', marginBottom: '4px' }
                    : {}
                }
                value={values[name]}
                onChange={(e) => onChangeInput({ name, value: e.target.value })}
                placeholder={placeholder}
                required={required}
                hasClearButton={false}
                disabled={name === 'refundAmount'}
                wrapperStyle={{
                  height: '36px',
                  border: hasInputError(name, {
                    isSupplyDateError,
                    isTouristNameError,
                    isPassportNumberError,
                    isBirthDateError,
                    isChartNumberError,
                  })
                    ? '1px solid #FF2D55'
                    : '0',
                  borderRadius: '12px',
                  padding: '9px 12px',
                  width: '242px',
                }}
                error={getErrorMessage(name, {
                  isTouristNameError,
                  isBirthDateError,
                  isSupplyDateError,
                  isPassportNumberError,
                  isChartNumberError,
                })}
                onFocus={() => {
                  if (name === 'productAmount') {
                    scrollToTop('productAmount');
                  }
                }}
                onBlur={() => {
                  if (name === 'birthDate') {
                    setIsBirthDateError(!isValidDate(values.birthDate));
                  }
                  if (name === 'supplyDate') {
                    setIsSupplyDateError(!isValidDate(values.supplyDate));
                  }
                }}
              />
            )}
          </InputItem>
        ))}
        {removeComma(refundAmount) !== 0 && (
          <InputItem>
            <Input
              label='환급액'
              value={refundAmount}
              onChange={() => {}}
              placeholder='자동 입력됩니다'
              hasClearButton={false}
              disabled
              wrapperStyle={{
                justifyContent: 'space-between',
                height: '36px',
                backgroundColor: 'transparent',
                borderRadius: '12px',
                padding: '9px 12px',
                border: `1px solid #CBCCCE`,
              }}
              addonAfter={<PriceUnit>원</PriceUnit>}
            />
          </InputItem>
        )}

        <InputItem>
          <TextArea
            label='환급 추가 요청 사유'
            name='reason'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder='환급 추가를 요청하게 된 상황을 설명해주시면 빠르고 정확한 처리가 가능해요'
            wrapperStyle={{
              backgroundColor: '#fff',
              padding: '9px 12px',
              border: '0',
            }}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              fontSize: '14px',
            }}
            rows={3}
            maxLength={65}
          />
        </InputItem>
      </InputList>

      <Footer>
        <SendButton onClick={onRequestCreateRefund} disabled={isDisabledButton}>
          <img src={ICON_SEND} alt='' width={20} />
          보내기
        </SendButton>
      </Footer>
    </Body>
  );
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: calc(100% - 27px - 18px); // Header, margin
`;

const Description = styled.p`
  width: 100%;
  color: #3a3b3e;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 10px;

  strong {
    font-weight: 700;
  }
`;

const InputList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InputItem = styled.li`
  label {
    color: #5f6165;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
  }

  input:disabled {
    background-color: transparent;
  }
  > p {
    margin-top: 0;
    justify-content: flex-end;

    svg {
      display: none;
    }
  }
`;

const PriceUnit = styled.span`
  color: #a7a7a7;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
`;

const Footer = styled.footer`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

const SendButton = styled.button`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  border-radius: 8px;
  transition: all 200ms;
  width: 100%;
  color: #fff;
  background-color: #246cf6;

  &:disabled {
    background-color: #d3e2fd;
  }
`;

export default RequestRefundSection;
